import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Boxes, ScanLine, ShieldAlert, Radar, Globe, Crosshair, Shield, Network, Activity, Inbox, HeartPulse, ChevronDown, LogOut, UserCircle2, TrendingDown, Route, Skull, KeyRound, Fingerprint } from "lucide-react";
import { useOrg } from "@/context/OrgContext";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = { super_admin: "Super Admin", org_admin: "Org Admin", analyst: "Analyst", client_viewer: "Client" };

const APPSEC = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", end: true, testid: "nav-dashboard", cap: "appsec.dashboard" },
  { to: "/applications", icon: Boxes, label: "Applications", testid: "nav-applications", cap: "appsec.applications" },
  { to: "/scan", icon: ScanLine, label: "New Scan", testid: "nav-scan", cap: "appsec.scans", level: "manage" },
  { to: "/findings", icon: ShieldAlert, label: "Findings", testid: "nav-findings", cap: "appsec.findings" },
  { to: "/exposure", icon: Radar, label: "Exposure Lookup", testid: "nav-exposure", cap: "appsec.exposure" },
];
const WEB = [
  { to: "/web/targets", icon: Globe, label: "Web Targets", testid: "nav-web-targets", cap: "web.targets" },
  { to: "/web/scan", icon: Crosshair, label: "New Web Scan", testid: "nav-web-scan", cap: "web.scans", level: "manage" },
];
const ATTACK = [
  { to: "/attack/domains", icon: Network, label: "Domains", testid: "nav-domains", cap: "attack.domains" },
  { to: "/attack/risk", icon: TrendingDown, label: "Risk Overview", testid: "nav-risk", cap: "attack.domains" },
];
const MONITORING = [
  { to: "/monitoring/inbox", icon: Inbox, label: "Alert Inbox", testid: "nav-mon-inbox", cap: "monitor.alerts" },
  { to: "/monitoring/impersonation", icon: Fingerprint, label: "Impersonation", testid: "nav-mon-impersonation", cap: "monitor.alerts" },
  { to: "/monitoring/watchers", icon: Activity, label: "Watchers", testid: "nav-mon-watchers", cap: "monitor.watchers" },
  { to: "/monitoring/routing", icon: Route, label: "BGP Routing", testid: "nav-mon-routing", cap: "monitor.watchers" },
  { to: "/monitoring/watchlist", icon: Skull, label: "Ransomware Watch", testid: "nav-mon-watchlist", cap: "monitor.watchers" },
  { to: "/monitoring/sources", icon: HeartPulse, label: "Sources Health", testid: "nav-mon-sources", cap: "monitor.sources" },
];

export function Wordmark() {
  return (
    <span className="font-display font-bold text-2xl leading-none" style={{ fontVariant: "small-caps" }}>
      <span style={{ color: "#A5300F" }}>Cyber</span><span style={{ color: "#E39629" }}>Lens</span>
    </span>
  );
}

const navCls = ({ isActive }) =>
  `flex items-center gap-3 px-5 py-2.5 text-sm transition-colors border-l-[3px] ${
    isActive ? "border-[#A5300F] bg-[#FFF5EB] text-[#A5300F] font-semibold"
             : "border-transparent text-[#5C554C] hover:bg-[#FBF7F1] hover:text-[#A5300F]"}`;

function NavGroup({ title, items, can }) {
  const visible = items.filter((n) => can(n.cap, n.level || "view"));
  if (visible.length === 0) return null;
  return (
    <div className="py-2">
      <div className="px-5 pb-1 text-[9px] tracking-[0.18em] uppercase text-[#B5AC9E] font-bold">{title}</div>
      {visible.map((n) => (
        <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} className={navCls}>
          <n.icon className="w-4 h-4" /> {n.label}
        </NavLink>
      ))}
    </div>
  );
}

export default function Shell({ children }) {
  const { orgs, orgId, selectOrg } = useOrg();
  const { user, logout, can, isAdmin } = useAuth();
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="flex h-screen bg-[#FBFBFB] text-[#323232] overflow-hidden">
      <aside className="w-60 shrink-0 bg-white border-r border-[#ECE6DD] flex flex-col">
        <div className="h-[68px] flex flex-col justify-center px-5 border-b border-[#ECE6DD]">
          <Wordmark />
          <span className="text-[9px] tracking-[0.18em] uppercase text-[#8A8175] font-semibold mt-1">
            a CyberNxt platform
          </span>
        </div>
        <nav className="flex-1 overflow-y-auto py-2 divide-y divide-[#F4EFE8]">
          <NavGroup title="Application Security" items={APPSEC} can={can} />
          <NavGroup title="Web Exposure & Integrity" items={WEB} can={can} />
          <NavGroup title="Attack Surface & Email Security" items={ATTACK} can={can} />
          <NavGroup title="Continuous Monitoring" items={MONITORING} can={can} />
          {isAdmin() && (
            <div className="py-2">
              <div className="px-5 pb-1 text-[9px] tracking-[0.18em] uppercase text-[#B5AC9E] font-bold">Administration</div>
              <NavLink to="/admin" data-testid="nav-admin" className={navCls}><Shield className="w-4 h-4" /> Admin Panel</NavLink>
            </div>
          )}
        </nav>
        <div className="p-4 text-[10px] text-[#B5AC9E] border-t border-[#ECE6DD]">
          Application Security · Exposure Management
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-[68px] shrink-0 bg-white border-b border-[#ECE6DD] flex items-center justify-between px-6">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#8A8175] font-semibold">
            Security Scanning Platform
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <span className="text-[11px] uppercase tracking-wider text-[#8A8175] font-semibold">Organisation</span>
              <div className="relative">
                <select data-testid="org-selector" value={orgId} onChange={(e) => selectOrg(e.target.value)}
                  disabled={orgs.length <= 1}
                  className="appearance-none bg-[#FFF5EB] border border-[#E7D9C6] rounded-md pl-3 pr-8 py-1.5 text-sm font-semibold text-[#A5300F] focus:outline-none disabled:opacity-80">
                  {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                </select>
                <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-[#A5300F] pointer-events-none" />
              </div>
            </div>
            <div className="relative">
              <button onClick={() => setMenu(!menu)} data-testid="user-menu-btn"
                className="flex items-center gap-2 text-sm text-[#5C554C] hover:text-[#A5300F]">
                <UserCircle2 className="w-6 h-6" />
                <div className="text-left leading-tight hidden sm:block">
                  <div className="font-semibold text-xs">{user?.name}</div>
                  <div className="text-[10px] text-[#8A8175]">{ROLE_LABEL[user?.role] || user?.role}</div>
                </div>
                <ChevronDown className="w-4 h-4" />
              </button>
              {menu && (
                <div className="absolute right-0 mt-2 w-56 bg-white border border-[#ECE6DD] rounded-md shadow-lg z-50" data-testid="user-menu">
                  <div className="px-4 py-3 border-b border-[#F4EFE8]">
                    <div className="text-sm font-semibold">{user?.name}</div>
                    <div className="text-xs text-[#8A8175]">{user?.email}</div>
                  </div>
                  <button onClick={() => { setMenu(false); navigate("/account/password"); }} data-testid="account-security-btn"
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#5C554C] hover:bg-[#FBF7F1]">
                    <KeyRound className="w-4 h-4" /> Account &amp; password
                  </button>
                  <button onClick={logout} data-testid="logout-btn"
                    className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#A5300F] hover:bg-[#FBF7F1] border-t border-[#F4EFE8]">
                    <LogOut className="w-4 h-4" /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
